<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-calendar animated bounceInDown show-info"></i>
						Attendance Holidays
					</h3>
					<p class="text-muted" style="margin-left:4px">
						Holidays are paid rest days. Working one earns overtime at the holiday multiplier.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add holiday
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Year</label>
					<div class="flex-row" style="display:flex;align-items:center">
						<button class="btn btn-default" @click="stepYear(-1)">
							<i class="fa fa-chevron-left"></i>
						</button>
						<span class="text-center font-weight-bold" style="min-width:64px">{{ year }}</span>
						<button class="btn btn-default" @click="stepYear(1)">
							<i class="fa fa-chevron-right"></i>
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Date</th>
							<th>Occasion</th>
							<th>Applies to</th>
							<th>Type</th>
							<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="5" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="5" class="text-center">No holidays recorded for {{ year }}.</td>
							</tr>
							<tr v-for="row in rows" :key="row.id">
								<td>{{ formatDate(row.holiday_date) }}</td>
								<td>{{ row.name }}</td>
								<td>{{ locationName(row.location_id) }}</td>
								<td>
									<span class="badge" :class="row.is_optional ? 'bg-warning text-dark' : 'bg-secondary'">
										{{ row.is_optional ? 'Optional' : 'Company-wide' }}
									</span>
								</td>
								<td style="white-space:nowrap">
									<button class="btn btn-sm btn-primary" @click="edit(row)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(row)" title="Remove">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="attendance-holiday" :width="480" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">{{ form.id ? 'Edit holiday' : 'Add holiday' }}</h4>
				<div class="form-group">
					<label>Date</label>
					<input type="date" class="form-control form-cascade-control" v-model="form.holiday_date" />
				</div>
				<div class="form-group">
					<label>Occasion</label>
					<input type="text" class="form-control form-cascade-control" placeholder="Independence Day" v-model="form.name" />
				</div>
				<div class="form-group">
					<label>Applies to</label>
					<select class="form-control form-cascade-control" v-model="form.location_id">
						<option :value="null">All locations</option>
						<option v-for="l in locations" :key="l.id" :value="l.id">{{ l.name }}</option>
					</select>
					<small class="text-muted">
						Site-specific holidays apply only to employees assigned to that location.
					</small>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.is_optional" />
						Optional — employees opt in rather than getting it automatically
					</label>
				</div>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-holiday')">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">Save</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { holidayService, locationService, errorMessage } from './attendanceService'
import attendanceFormat from './attendanceFormat'

export default {
	name: 'AttendanceHolidays',
	mixins: [attendanceFormat],
	data() {
		return {
			year: new Date().getFullYear(),
			rows: [],
			locations: [],
			loading: false,
			error: '',
			saving: false,
			form: this.blank(),
		}
	},
	mounted() {
		this.loadLocations()
		this.refresh()
	},
	methods: {
		blank() {
			return {
				id: null,
				holiday_date: this.toIsoDate(new Date()),
				name: '',
				is_optional: false,
				location_id: null,
			}
		},
		refresh() {
			this.loading = true
			this.error = ''
			holidayService
				.list({ year: this.year })
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load holidays.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		loadLocations() {
			locationService
				.list()
				.then((data) => {
					this.locations = data || []
				})
				.catch(() => {})
		},
		stepYear(delta) {
			this.year += delta
			this.refresh()
		},
		create() {
			this.form = this.blank()
			this.$modal.show('attendance-holiday')
		},
		edit(holiday) {
			this.form = {
				...holiday,
				holiday_date: String(holiday.holiday_date).slice(0, 10),
			}
			this.$modal.show('attendance-holiday')
		},
		save() {
			this.saving = true
			const body = { ...this.form }
			const id = body.id
			delete body.id
			body.holiday_date = this.toIsoDate(new Date(body.holiday_date))
			const req = id ? holidayService.update(id, body) : holidayService.create(body)
			req
				.then(() => {
					this.$notify({ text: 'Saved', type: 'success' })
					this.$modal.hide('attendance-holiday')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		remove(holiday) {
			if (!window.confirm(`Remove "${holiday.name}"? Recompute the affected dates afterwards.`))
				return
			holidayService
				.remove(holiday.id)
				.then(() => {
					this.$notify({ text: 'Removed', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
		locationName(id) {
			if (!id) return 'All locations'
			const found = this.locations.find((l) => l.id === id)
			return found ? found.name : `#${id}`
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
</style>
